import React, { useCallback, useState, createContext, useContext } from 'react';
export interface CartItem {
  id: string;
  category: string;
  product: string;
  quantity: number;
  pricePerYard: number;
}
interface CartContextType {
  items: CartItem[];
  isCartOpen: boolean;
  addToCart: (item: Omit<CartItem, 'id'>) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  loadSavedCart: (savedItems: CartItem[]) => void;
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  totalItems: number;
  totalPrice: number;
  totalYards: number;
  deliveryCount: number;
  deliveryAddress: string;
  deliveryFee: number;
  deliveryZone: number | null;
  isCalculatingDelivery: boolean;
  setDeliveryInfo: (address: string, fee: number, zone: number | null) => void;
  setIsCalculatingDelivery: (isCalculating: boolean) => void;
  clearDeliveryInfo: () => void;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryZone, setDeliveryZone] = useState<number | null>(null);
  const [isCalculatingDelivery, setIsCalculatingDeliveryState] = useState(false);
  const setDeliveryInfo = useCallback((address: string, fee: number, zone: number | null) => {
    setDeliveryAddress(address);
    setDeliveryFee(fee);
    setDeliveryZone(zone);
  }, []);
  const clearDeliveryInfo = useCallback(() => {
    setDeliveryAddress('');
    setDeliveryFee(0);
    setDeliveryZone(null);
  }, []);
  const setIsCalculatingDelivery = useCallback((isCalculating: boolean) => {
    setIsCalculatingDeliveryState(isCalculating);
  }, []);
  const addToCart = (newItem: Omit<CartItem, 'id'>) => {
    setItems(prevItems => {
      // Generate a unique ID for each cart entry so same materials aren't merged
      const id = `${newItem.category}-${newItem.product}-${Date.now()}`;
      return [...prevItems, {
        ...newItem,
        id
      }];
    });
  };
  const removeFromCart = (id: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
  };
  const updateQuantity = (id: string, quantity: number) => {
    const item = items.find(i => i.id === id);
    const n = item?.product.toLowerCase() || '';
    const isLight = n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('50/50') || n.includes('fill dirt') || n.includes('play chips');
    const minQty = 5;
    const maxQty = isLight ? 60 : 48;
    if (quantity < minQty || quantity > maxQty) {
      return; // Min/max yards enforcement
    }
    setItems(prevItems => prevItems.map(item => item.id === id ? {
      ...item,
      quantity
    } : item));
  };
  const clearCart = () => {
    setItems([]);
    clearDeliveryInfo();
  };
  const loadSavedCart = (savedItems: CartItem[]) => {
    setItems(savedItems);
  };
  const toggleCart = () => setIsCartOpen(prev => !prev);
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const totalItems = items.length;
  const totalPrice = (() => {
    // Aggregate total yards per product for bulk discount calculation
    const productYards: Record<string, number> = {};
    const baseProductName = (name: string) => name.replace(/\s*\(Delivery \d+\)/, '');
    for (const item of items) {
      const base = baseProductName(item.product);
      productYards[base] = (productYards[base] || 0) + item.quantity;
    }
    return items.reduce((sum, item) => {
      const n = item.product.toLowerCase();
      const isLight = n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('50/50') || n.includes('fill dirt') || n.includes('play chips');
      const minChargeYards = 5;
      const smallLoadThreshold = isLight ? 12 : 10;
      const billableYards = Math.max(item.quantity, minChargeYards);
      const surcharge = billableYards < smallLoadThreshold ? 1 + (smallLoadThreshold - billableYards) * 0.1 : 1;
      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
      let itemTotal = billableYards * effectivePrice;
      // Tiered bulk discount: light materials 5% at 15yds, others 5% at 24yds; 10% at 4 full loads
      const aggYards = productYards[baseProductName(item.product)] || item.quantity;
      const fullLoadYards = isLight ? 15 : 12;
      const bulkPct = aggYards >= fullLoadYards * 4 ? 0.1 : aggYards >= (isLight ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      if (bulkPct > 0) {
        itemTotal = Math.round(itemTotal * (1 - bulkPct));
      }
      return sum + itemTotal;
    }, 0);
  })();
  const totalYards = items.reduce((sum, item) => sum + item.quantity, 0);
  const lightYards = items.reduce((sum, item) => {
    const n = item.product.toLowerCase();
    const isLight = n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('50/50') || n.includes('fill dirt') || n.includes('play chips');
    return isLight ? sum + item.quantity : sum;
  }, 0);
  const otherYards = totalYards - lightYards;
  const deliveryCount = (lightYards > 0 ? Math.ceil(lightYards / 15) : 0) + (otherYards > 0 ? Math.ceil(otherYards / 12) : 0) || 1;
  return <CartContext.Provider value={{
    items,
    isCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    loadSavedCart,
    toggleCart,
    openCart,
    closeCart,
    totalItems,
    totalPrice,
    totalYards,
    deliveryCount,
    deliveryAddress,
    deliveryFee,
    deliveryZone,
    isCalculatingDelivery,
    setDeliveryInfo,
    setIsCalculatingDelivery,
    clearDeliveryInfo
  }} data-id="element-148">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}